// frontend/src/pages/MetaCubeOraclePage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import MetaCubeOracleV1 from "../components/MetaCubeOracleV1";
import MetaCubeOraclePlaylist from "../components/MetaCubeOraclePlaylist";

export default function MetaCubeOraclePage() {
  // États entropie
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);

  // États curseurs
  const [cubeSpeedPct, setCubeSpeedPct] = useState(80);
  const [ringSpeedPct, setRingSpeedPct] = useState(50);
  const [colorShiftRatePct, setColorShiftRatePct] = useState(30);

  // Options
  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);
  const [playlistMode, setPlaylistMode] = useState(false); // 🎵 nouveau mode

  // Sliders Bloom
  const [bloomIntensity, setBloomIntensity] = useState(1.2);
  const [bloomRadius, setBloomRadius] = useState(0.6);
  const [bloomThreshold, setBloomThreshold] = useState(0.0);

  const canvasRef = useRef(null);
  const orbitRef = useRef(null);

  // --- Entropie UI (API backend) ---
  useEffect(() => {
    setLoadingUI(true);
    const controller = new AbortController();

    fetch("/api/geometry/metacube_oracle/entropy_ui", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        rotation_speed: cubeSpeedPct / 100,
        scale_factor: ringSpeedPct / 100,
        glow,
        chaos,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (typeof data.entropy_ui === "number") {
          setEntropyUI(data.entropy_ui);
        }
      })
      .catch(() => setEntropyUI(null))
      .finally(() => setLoadingUI(false));

    return () => controller.abort();
  }, [
    chaos,
    glow,
    cubeSpeedPct,
    ringSpeedPct,
    colorShiftRatePct,
    bloomIntensity,
    bloomRadius,
    bloomThreshold,
  ]);

  // ⚡ Score total
  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  return (
    <div className="page-container">
      <h1>MetaCube Oracle V1</h1>

      {/* Jauges */}
      <div className="gauges-row">
        <div className="card small">
          <h2>Entropie UI</h2>
          <div className="value ui">
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#ff0266" }}>Entropie Dynamique</h2>
          <div className="value dyn">
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#29b6f6" }}>Score Total</h2>
          <div className="value total">{entropyTotal}</div>
        </div>
      </div>

      {/* Contrôles + Canvas */}
      <div className="controls-row">
        {/* Contrôles */}
        <div className="controls-panel">
          <button
            className="generate-button"
            onClick={() => setRunning((p) => !p)}
          >
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            onClick={() => setChaos((p) => !p)}
            className={chaos ? "toggle-button active-chaos" : "toggle-button inactive-chaos"}
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            onClick={() => setGlow((p) => !p)}
            className={glow ? "toggle-button active-glow" : "toggle-button inactive-glow"}
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          {/* 🎵 Bouton Playlist Oracle */}
          <button
            onClick={() => setPlaylistMode((p) => !p)}
            style={{
              padding: "12px 18px",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: playlistMode ? "#6200ee" : "#bb86fc",
              fontWeight: "bold",
            }}
          >
            {playlistMode ? "⏹️ Stop Playlist" : "🎵 Playlist Oracle"}
          </button>

          {/* Sliders */}
          <div className="input-group">
            <label>Vitesse Cube : {cubeSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={150}
              value={cubeSpeedPct}
              onChange={(e) => setCubeSpeedPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Vitesse Anneaux : {ringSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={150}
              value={ringSpeedPct}
              onChange={(e) => setRingSpeedPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Halo Chromatique : {colorShiftRatePct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={colorShiftRatePct}
              onChange={(e) => setColorShiftRatePct(Number(e.target.value))}
            />
          </div>

          {/* Sliders Bloom */}
          <div className="input-group">
            <label>Halo Intensité : {bloomIntensity.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={3}
              step={0.1}
              value={bloomIntensity}
              onChange={(e) => setBloomIntensity(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Halo Rayon : {bloomRadius.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={bloomRadius}
              onChange={(e) => setBloomRadius(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Seuil Luminosité : {bloomThreshold.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={bloomThreshold}
              onChange={(e) => setBloomThreshold(Number(e.target.value))}
            />
          </div>
        </div>

        {/* Canvas ou Playlist */}
        <div className="metacube-v1-canvas">
          {playlistMode ? (
            <MetaCubeOraclePlaylist />
          ) : (
            <Canvas
              ref={canvasRef}
              camera={{ position: [0, 15, 40], fov: 60 }}
              style={{ width: 600, height: 480, borderRadius: "10px" }}
            >
              <ambientLight intensity={0.7} />
              <pointLight position={[30, 40, 30]} intensity={1.2} />
              <MetaCubeOracleV1
                running={running}
                chaos={chaos}
                glow={glow}
                cubeSpeedPct={cubeSpeedPct}
                ringSpeedPct={ringSpeedPct}
                colorShiftRatePct={colorShiftRatePct}
                bloomIntensity={bloomIntensity}
                bloomRadius={bloomRadius}
                bloomThreshold={bloomThreshold}
                onEntropyUpdate={(val) => setEntropyDyn(val)}
              />
              <OrbitControls
                ref={orbitRef}
                enableZoom={true}
                enablePan={true}
                minDistance={5}
                maxDistance={100}
                target={[0, 0, 0]}
              />
            </Canvas>
          )}
        </div>
      </div>
    </div>
  );
}
